import {Injectable, inject} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, forkJoin, map, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '@formrails/environments';
import {ApplicationTemplateDto as ApplicationTemplateListDto, FormTemplateType} from '../models';
import {ApplicationTemplateDto, CreateApplicationTemplateDto} from '@formrails/shared/data-access';
import {
    FormTemplateResponseWireDto,
    fromFormTemplateListItem,
    fromFormTemplateResponse,
    toUpsertFormTemplateDto
} from './form-template-api.mappers';

/**
 * Template access routed across two backends:
 * - Application templates ('Application') live on the Application API
 *   (/ApplicationTemplate) — unchanged.
 * - Every other form type (Assessment, Eligibility, Pre-assessment, Survey,
 *   User Profile, ...) lives on the Forms API (/FormTemplate), which uses
 *   integer enums and an `elements` question model; the wire mappers
 *   translate so callers keep working with the application-template shapes.
 *
 * Routing for by-id reads uses the template-type cache populated by
 * `getForms()`; unknown ids fall back to Application API first, then the
 * Forms API.
 */
@Injectable({
    providedIn: 'root'
})
export class FormApiService {
    private http = inject(HttpClient);
    private apiUrl = environment.applicationApi;
    private formApiUrl = environment.formApi;

    /** Template id → form type, learned from list responses. */
    private readonly templateTypes = new Map<string, FormTemplateType>();

    /**
     * Fetch all templates: application templates from the Application API
     * merged with every other form type from the Forms API. Either source
     * failing yields the other's results rather than a hard error.
     */
    getForms(): Observable<ApplicationTemplateListDto[]> {
        const applicationTemplates$ = this.http
            .get<ApplicationTemplateListDto[]>(`${this.apiUrl}/ApplicationTemplate`)
            .pipe(
                catchError(err => {
                    console.error('[FormApiService] Application API template list failed:', err);
                    return of([] as ApplicationTemplateListDto[]);
                })
            );

        const formTemplates$ = this.http
            .get<FormTemplateResponseWireDto[]>(`${this.formApiUrl}/FormTemplate`)
            .pipe(
                map(list => (list ?? []).filter(t => !t.deleted).map(fromFormTemplateListItem)),
                catchError(err => {
                    console.error('[FormApiService] Forms API template list failed:', err);
                    return of([] as ApplicationTemplateListDto[]);
                })
            );

        return forkJoin([applicationTemplates$, formTemplates$]).pipe(
            map(([applications, forms]) => {
                const merged = [
                    ...applications.map(t => ({
                        ...t,
                        formTemplateType: t.formTemplateType ?? ('Application' as FormTemplateType)
                    })),
                    ...forms
                ];
                for (const t of merged) {
                    this.templateTypes.set(t.id, t.formTemplateType ?? 'Application');
                }
                return merged;
            })
        );
    }

    /**
     * Fetch a template by id, routed by its form type.
     */
    /**
     * Records that a requested promotion has been signed off. The worker picks
     * approved versions up from there.
     */
    approvePromotion(templateId: string): Observable<ApplicationTemplateDto> {
        return this.http.patch<ApplicationTemplateDto>(
            `${this.apiUrl}/applicationtemplate/${templateId}/promote/approve`, {}
        );
    }

    /**
     * Asks for a published application template to be promoted to production.
     * The version then waits on the client's sign-off, which is what sends it
     * onward, so this only records the request.
     */
    requestPromotion(templateId: string): Observable<ApplicationTemplateDto> {
        return this.http.patch<ApplicationTemplateDto>(
            `${this.apiUrl}/applicationtemplate/${templateId}/promote`, {}
        );
    }

    getFormByTemplateId(templateId: string): Observable<ApplicationTemplateDto> {
        if (this.isFormApiTemplate(templateId)) {
            return this.getFormApiTemplate(templateId);
        }
        return this.http
            .get<ApplicationTemplateDto>(`${this.apiUrl}/applicationtemplate/${templateId}`)
            .pipe(catchError(err => this.fallbackToFormApi(templateId, err)));
    }

    /**
     * Fetch the raw (unresolved) template by id. The Forms API has no /Raw
     * variant — its single representation is already raw.
     */
    getRawFormByTemplateId(templateId: string): Observable<ApplicationTemplateDto> {
        if (this.isFormApiTemplate(templateId)) {
            return this.getFormApiTemplate(templateId);
        }
        return this.http
            .get<ApplicationTemplateDto>(`${this.apiUrl}/applicationtemplate/${templateId}/Raw`)
            .pipe(catchError(err => this.fallbackToFormApi(templateId, err)));
    }

    /**
     * Create a template. Application templates go to the Application API;
     * everything else goes to the Forms API.
     */
    createApplicationTemplate(
        dto: CreateApplicationTemplateDto,
        formType: FormTemplateType = 'Application'
    ): Observable<ApplicationTemplateDto> {
        if (formType !== 'Application') {
            return this.http
                .post<FormTemplateResponseWireDto>(
                    `${this.formApiUrl}/FormTemplate`,
                    toUpsertFormTemplateDto(dto, formType)
                )
                .pipe(map(wire => this.rememberAndMap(wire, formType)));
        }
        return this.http.post<ApplicationTemplateDto>(`${this.apiUrl}/applicationtemplate`, dto);
    }

    /**
     * Update a template, routed the same way as create.
     */
    updateApplicationTemplate(
        templateId: string,
        dto: CreateApplicationTemplateDto,
        formType: FormTemplateType = 'Application'
    ): Observable<ApplicationTemplateDto> {
        if (formType !== 'Application') {
            return this.http
                .put<FormTemplateResponseWireDto>(
                    `${this.formApiUrl}/FormTemplate/${templateId}`,
                    toUpsertFormTemplateDto(dto, formType)
                )
                .pipe(map(wire => this.rememberAndMap(wire, formType)));
        }
        return this.http.put<ApplicationTemplateDto>(
            `${this.apiUrl}/applicationtemplate/${templateId}`,
            dto
        );
    }

    // --- Private ---

    private isFormApiTemplate(templateId: string): boolean {
        const type = this.templateTypes.get(templateId);
        return !!type && type !== 'Application';
    }

    private getFormApiTemplate(templateId: string): Observable<ApplicationTemplateDto> {
        return this.http
            .get<FormTemplateResponseWireDto>(`${this.formApiUrl}/FormTemplate/${templateId}`)
            .pipe(map(fromFormTemplateResponse));
    }

    /**
     * Deep links can reference a template never listed in this session; when
     * the Application API 404s, try the Forms API before giving up.
     */
    private fallbackToFormApi(
        templateId: string,
        err: { status?: number }
    ): Observable<ApplicationTemplateDto> {
        if (err?.status === 404) {
            return this.getFormApiTemplate(templateId);
        }
        throw err;
    }

    private rememberAndMap(
        wire: FormTemplateResponseWireDto,
        formType: FormTemplateType
    ): ApplicationTemplateDto {
        this.templateTypes.set(wire.id, formType);
        return fromFormTemplateResponse(wire);
    }
}
